import React, { useMemo } from "react";

import { chunk } from "lodash";

import { MetricCategory, type MetricConfig } from "../metric-category-builder";

import { DefaultMetricsSelector } from "./default-metrics-selector";
import { FbMetricsSelector } from "./fb-metrics-selector";

interface MetricVisibilityPanelProps<T extends MetricConfig> {
  visibilityOfMetrics: T[];
  selectedCategory: MetricCategory;
  toggleMetricVisibility: (arg0: T) => void;
  toggleVisibilityOfAllMetrics?: (columns: T[], visible: boolean) => void;
}

export const MetricVisibilityPanel = <T extends MetricConfig>({
  visibilityOfMetrics,
  toggleMetricVisibility,
  selectedCategory,
  toggleVisibilityOfAllMetrics,
}: MetricVisibilityPanelProps<T>) => {
  const COLUMN_COUNT = 2;

  const changeAllVisibility = (allVisibility: boolean) => {
    toggleVisibilityOfAllMetrics?.(visibilityOfMetrics, allVisibility);
  };

  const metricOrderedVisibility = useMemo(() => {
    if (visibilityOfMetrics.length < 3) {
      return visibilityOfMetrics;
    }

    const newOrder = [];
    const numPermetric = Math.ceil(visibilityOfMetrics.length / COLUMN_COUNT);
    const metricsChunked = chunk(
      visibilityOfMetrics.sort((a, b) => {
        /* HACK HACK HACK
           To get strings to sort correctly with special characters, I need to strip those
           characters here.  Otherwise "FB Shops purchases (7D Click)" will come _after_ "FB Shops
           purchases (7D Click, 1D View)" */
        return a.name
          .replace(/[^\w\s]/gi, "")
          .localeCompare(b.name.replace(/[^\w\s]/gi, ""));
      }),

      numPermetric,
    );
    for (let i = 0; i < numPermetric; i++) {
      for (let j = 0; j < COLUMN_COUNT; j++) {
        if (metricsChunked[j][i]) {
          newOrder.push(metricsChunked[j][i]);
        }
      }
    }

    return newOrder;
  }, [visibilityOfMetrics]);

  if (visibilityOfMetrics.length === 0) {
    return (
      <div data-testid="no-metric-visibility-panel">No metrics to display</div>
    );
  }

  return selectedCategory === MetricCategory.FACEBOOK ? (
    <FbMetricsSelector
      metricOrderedVisibility={metricOrderedVisibility}
      toggleMetricVisibility={toggleMetricVisibility}
    />
  ) : (
    <DefaultMetricsSelector
      metricOrderedVisibility={metricOrderedVisibility}
      toggleMetricVisibility={toggleMetricVisibility}
      changeAllVisibility={changeAllVisibility}
    />
  );
};
